import { atom } from "jotai";
import { JotaiTool } from "../../../../index";

export const tablesOperationsToolsAtom = atom<JotaiTool<any>[]>(() => [
  // TODO: Add specific tools for tables operations
  // Example: get(networkRequestAtom), get(getTableByIdAtom), etc.
]);
tablesOperationsToolsAtom.debugLabel = "tablesOperationsToolsAtom";

export const tablesOperationsRecipe = `# Tables Operations: Creating and managing Zapier Tables

## Overview
Create and manage Zapier Tables including table creation, field management, record operations, and usage limit handling. Tables provide data storage capabilities for Canvas workflows.

## Key API Endpoints

### GET tables/tables
- **Purpose**: List all tables owned by the user
- **Authentication**: Uses session cookies via network_request tool
- **Example**: \`network_request({ method: "GET", api: "tables", path: "/tables" })\`

### POST tables/tables
- **Purpose**: Create a new table with basic configuration
- **Authentication**: Uses session cookies via network_request tool
- **Important**: Fields passed during table creation are NOT actually created - you must call POST /tables/{table_id}/fields separately
- **Example**: \`network_request({ method: "POST", api: "tables", path: "/tables" })\`

### GET tables/tables/{table_id}
- **Purpose**: Retrieve table details including fields, records count, and metadata
- **Authentication**: Uses session cookies via network_request tool
- **Example**: \`network_request({ method: "GET", api: "tables", path: "/tables/{table_id}" })\`

### PATCH tables/tables/{table_id}
- **Purpose**: Update table properties, add/modify fields, or change configuration
- **Authentication**: Uses session cookies via network_request tool
- **Example**: \`network_request({ method: "PATCH", api: "tables", path: "/tables/{table_id}" })\`

### DELETE tables/tables/{table_id}
- **Purpose**: Delete a table permanently
- **Authentication**: Uses session cookies via network_request tool
- **Example**: \`network_request({ method: "DELETE", api: "tables", path: "/tables/{table_id}" })\`

### GET tables/tables/{table_id}/records
- **Purpose**: Retrieve records from the table with filtering and pagination
- **Authentication**: Uses session cookies via network_request tool
- **Example**: \`network_request({ method: "GET", api: "tables", path: "/tables/{table_id}/records" })\`

### POST tables/tables/{table_id}/records
- **Purpose**: Bulk create multiple records in the table with field data
- **Authentication**: Uses session cookies via network_request tool
- **Body Structure**: Must include \`new_records\` array wrapper
- **Field References**: Use \`fX\` format (f1, f2, f3) NOT numeric IDs (1, 2, 3)
- **Example**: \`network_request({ method: "POST", api: "tables", path: "/tables/{table_id}/records", body: { new_records: [...] } })\`

### POST tables/tables/{table_id}/fields
- **Purpose**: Create new fields in the table
- **Authentication**: Uses session cookies via network_request tool
- **Body Structure**: Must include \`new_fields\` array wrapper
- **Example**: \`network_request({ method: "POST", api: "tables", path: "/tables/{table_id}/fields", body: { new_fields: [...] } })\`

## Implementation Steps

1. **Create table** using POST /tables with name and description only
2. **Extract table_id** from creation response or use GET /tables to find the new table
3. **Create fields** using POST /tables/{table_id}/fields with new_fields array (REQUIRED STEP)
4. **Update canvas** with table details (tableId, field mappings)
5. **Optionally configure field properties** using PATCH endpoint if needed
6. **Handle record operations** for creating, reading, updating table data
7. **Monitor usage limits** and implement appropriate error handling
8. **Create visual connections** between table and other Canvas nodes

### Critical Two-Step Process
⚠️ **IMPORTANT**: Table creation and field creation are separate API calls:
- Step 1: Create empty table with POST /tables
- Step 2: Add fields with POST /tables/{table_id}/fields

Even if you include a 'fields' array in the table creation request, those fields will NOT be created automatically.

## Critical Patterns

### Table Creation Structure (POST /tables)
\`\`\`
{
  name: 'Table Name',
  description: 'Table description'
  // NOTE: Do not include 'fields' array here - fields must be created separately
}
\`\`\`

### Complete Table Setup Workflow
\`\`\`javascript
// Step 1: Create table (returns table with ID)
const tableResponse = await network_request({
  method: "POST",
  api: "tables", 
  path: "/tables",
  body: {
    name: "Birthday Party Reservations",
    description: "Track birthday party bookings and details"
  }
});
const tableId = tableResponse.data.id;

// Step 2: Create fields (REQUIRED - separate API call)
const fieldsResponse = await network_request({
  method: "POST",
  api: "tables",
  path: \`/tables/\${tableId}/fields\`,
  body: {
    new_fields: [
      {
        name: 'Customer Name',
        type: 'text',
        required: true,
        description: 'Full name of the customer'
      },
      {
        name: 'Email',
        type: 'email', 
        required: true,
        description: 'Contact email address'
      }
      // ... more fields
    ]
  }
});
\`\`\`

### Field Creation Structure (POST /tables/{table_id}/fields)
\`\`\`
{
  new_fields: [
    {
      name: 'Customer Name',
      type: 'text',
      required: true,
      description: 'Full name of the customer'
    },
    {
      name: 'Email',
      type: 'email',
      required: true,
      description: 'Contact email address'
    },
    {
      name: 'Phone',
      type: 'phone_number',
      required: false,
      description: 'Contact phone number'
    },
    {
      name: 'Birthday',
      type: 'datetime',
      required: false,
      description: 'Date of birth'
    },
    {
      name: 'Total Cost',
      type: 'currency',
      required: false,
      description: 'Total amount'
    }
  ]
}
\`\`\`

### Record Creation Structure (POST /tables/{table_id}/records)
\`\`\`
{
  new_records: [
    {
      data: {
        'f1': 'value1',        // First field (use f1, not 1)
        'f2': 'value2',        // Second field (use f2, not 2)
        'f3': 123,             // Third field (use f3, not 3)
        'f4': 'another_value'  // Fourth field (use f4, not 4)
      }
    },
    {
      data: {
        'f1': 'second_record_value1',
        'f2': 'second_record_value2',
        'f3': 456,
        'f4': 'second_record_value4'
      }
    }
  ]
}
\`\`\`

### Field Reference Pattern  
\`\`\`
// ❌ WRONG: Using numeric IDs (causes "Unexpected field provided" error)
{
  data: {
    '1': 'value',
    '2': 'another_value'
  }
}

// ✅ CORRECT: Using fX format 
{
  data: {
    'f1': 'value',       // First field created
    'f2': 'another_value' // Second field created
  }
}
\`\`\`

### Complete Record Creation Example
\`\`\`javascript
// Create a record with proper field references
const recordResponse = await network_request({
  method: "POST",
  api: "tables",
  path: \`/tables/\${tableId}/records\`,
  body: {
    new_records: [
      {
        data: {
          'f1': 'Buddy',                           // Pet Name (first field)
          'f2': 'Golden Retriever',                // Breed (second field)  
          'f3': 3,                                 // Age (third field)
          'f4': 'Sarah Johnson',                   // Owner Name (fourth field)
          'f5': 'sarah.johnson@email.com',         // Email (fifth field)
          'f6': '+1-555-123-4567',                 // Phone (sixth field)
          'f7': '2024-09-15T14:00:00Z',            // Appointment Date (seventh field)
          'f8': 8,                                 // Number of guests (eighth field)
          'f9': 'Peanut-free treats please',       // Special Requests (ninth field)
          'f10': 150                               // Total Cost (tenth field)
        }
      }
    ]
  }
});
\`\`\`

## Important Notes

- **Two-step process REQUIRED**: Always create table first, then create fields in separate API call
- **Field creation is mandatory**: Tables created without fields cannot store records
- **Field IDs follow fX pattern** (f1, f2, f3) for referencing in records and other components
- **Record creation requires new_records wrapper** - similar to field creation requiring new_fields
- **Use fX field keys in records** - NOT numeric IDs (1, 2, 3) which cause "Unexpected field" errors
- **Monitor usage limits** - tables have record count and storage limits
- **Use appropriate field types** to ensure proper validation and formatting  
- **Handle API rate limits** when performing bulk operations
- **Field modifications** may require table schema updates
- **Record operations support bulk creation** for efficiency
- **Always validate field data** before record creation to prevent errors

## Field Types and Validation

### Supported Field Types (from API)
- **text**: General text input with optional length limits
- **multiple_text**: Multiple text values
- **string**: Basic string type
- **multiple_string**: Multiple string values
- **labeled_string**: String with label options
- **multiple_labeled_string**: Multiple labeled strings
- **number**: Numeric values with optional min/max constraints
- **multiple_number**: Multiple numeric values
- **decimal**: Decimal number values
- **multiple_decimal**: Multiple decimal values
- **email**: Email validation with proper format checking
- **multiple_email**: Multiple email addresses
- **datetime**: Date and time values (use instead of "date")
- **multiple_datetime**: Multiple datetime values
- **boolean**: True/false values for checkboxes
- **multiple_boolean**: Multiple boolean values
- **link**: URL validation with proper format checking
- **multiple_link**: Multiple URL links
- **phone_number**: Phone number validation (use instead of "phone")
- **currency**: Currency values with formatting
- **uuid**: UUID values
- **multiple_uuid**: Multiple UUID values
- **json**: JSON data structures
- **multiple_json**: Multiple JSON objects
- **formula**: Calculated field formulas
- **button_trigger_zap**: Button to trigger Zap
- **button_continue_zap**: Button to continue Zap
- **ai_formula**: AI-powered formula fields
- **linked_record**: Reference to records in other tables
- **multiple_linked_record**: Multiple linked records

### Common Field Type Mappings
- Use **datetime** instead of "date"
- Use **phone_number** instead of "phone"
- Use **link** instead of "url"
- Use **currency** for monetary values instead of "number"

## Usage Limits and Monitoring

- **Record limits**: Monitor record count against plan limits
- **Storage limits**: Track data storage usage
- **API rate limits**: Implement proper retry logic
- **Field limits**: Maximum number of fields per table
- **Performance considerations**: Large tables may require pagination

## Troubleshooting

### Field Creation Errors
- **"field required" error**: Ensure request body includes \`new_fields\` wrapper array
- **"unexpected value" for field type**: Use correct API field types:
  - Use \`phone_number\` instead of \`phone\`
  - Use \`datetime\` instead of \`date\`
  - Use \`link\` instead of \`url\`
  - Use \`currency\` for monetary values

### Record Creation Errors
- **"field required" (new_records)**: Ensure request body includes \`new_records\` wrapper array
- **"Unexpected field provided"**: Use \`fX\` format for field keys (f1, f2, f3) NOT numeric IDs (1, 2, 3)
- **Field key mismatch**: Verify field order matches creation order (first field created = f1, second = f2, etc.)

### General API Errors
- **400 Bad Request**: Validate field types and required field values
- **403 Forbidden**: Check table permissions and usage limits
- **404 Not Found**: Verify table_id exists and is accessible
- **Field mapping issues**: Ensure correct field ID format (f1, f2, etc.)
- **Record creation failures**: Validate data against field types and requirements
- **Usage limit exceeded**: Implement upgrade prompts or data cleanup strategies`;
